import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/article';

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;
  const related = slug ? getRelatedArticles(slug, 3) : [];

  const categoryLabels: Record<string, string> = {
    ranking: '排行榜',
    industry: '产业观察',
    review: '动漫评论',
    guide: '追番指南',
  };

  if (!article) {
    return (
      <Layout>
        <div className="pt-24 pb-14">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 text-center py-20">
            <p className="text-foreground-500">文章不存在</p>
            <Link to="/articles" className="mt-4 inline-block text-primary-500 hover:text-primary-600 text-sm cursor-pointer">
              返回文章列表
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <article className="pt-24 pb-14">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <Breadcrumb
              items={[
                { label: '动漫资讯', path: '/articles' },
                { label: categoryLabels[article.category] || article.category, path: '/articles' },
                { label: article.title },
              ]}
            />

            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                  '@context': 'https://schema.org',
                  '@type': 'Article',
                  headline: article.title,
                  description: article.excerpt,
                  image: article.cover,
                  author: { '@type': 'Person', name: article.author },
                  datePublished: article.publishDate,
                  publisher: {
                    '@type': 'Organization',
                    name: '小樱动漫',
                  },
                }),
              }}
            />

            <header className="mb-8">
              <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium mb-4">
                {categoryLabels[article.category] || article.category}
              </span>
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-950 leading-tight">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-3 mt-4 text-sm text-foreground-500">
                <span>{article.author}</span>
                <span>·</span>
                <span>{article.publishDate}</span>
                <span>·</span>
                <span>{article.readTime}</span>
              </div>
            </header>

            <div className="rounded-lg overflow-hidden mb-10">
              <img
                src={article.cover}
                alt={article.title}
                className="w-full aspect-[16/9] object-cover"
              />
            </div>

            <div
              className="prose prose-sm max-w-none text-foreground-800 leading-relaxed space-y-4"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-background-200">
              {article.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs">
                  #{tag}
                </span>
              ))}
            </div>
          </div>

          {related.length > 0 && (
            <div className="mt-14 pt-10 border-t border-background-200">
              <h3 className="text-lg font-heading text-foreground-950 mb-5">相关文章</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {related.map((item) => (
                  <ArticleCard key={item.id} article={item} />
                ))}
              </div>
            </div>
          )}
        </div>
      </article>
    </Layout>
  );
}